import {
  ArrowLeftIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

import type { PayoutBatch } from 'api';

import { ApprovalBadge } from '@atoms/badges/ApprovalBadge/ApprovalBadge';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { OrgLink } from '@atoms/OrgLink';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { ApprovalStatus } from './ApprovalStatus';

type Props = {
  toggleDeclineDialog: () => void;
  batchDetails: PayoutBatch | undefined;
  isApprovalEnabled: boolean;
  userId: number;
  requiredNumberOfApprovals: number;
  canApprove: boolean;
  formatBatchDetails: (batchDetails: PayoutBatch) => string;
  handleApprove: () => void;
};

export const BatchPayoutsHeader = ({
  toggleDeclineDialog,
  batchDetails,
  isApprovalEnabled,
  userId,
  requiredNumberOfApprovals,
  canApprove,
  formatBatchDetails,
  handleApprove
}: Props) => {
  const backUrl = isApprovalEnabled
    ? `/batches?status=${batchDetails?.status}`
    : '/batches';

  return (
    <div className="flex items-end justify-between">
      <div className="flex flex-col gap-4">
        <OrgLink
          to={backUrl}
          className="flex cursor-pointer items-center gap-2 font-medium text-content-brand"
        >
          <ArrowLeftIcon className="size-6" />
          Back
        </OrgLink>
        <h1 className="text-2xl font-medium">{batchDetails?.name}</h1>
        <div className="flex flex-row">
          <h2 className="text-lg font-medium">
            {batchDetails && formatBatchDetails(batchDetails)}
            {isApprovalEnabled && batchDetails?.status && (
              <span className="ml-3">
                <ApprovalBadge status={batchDetails?.status} />
              </span>
            )}
          </h2>

          {isApprovalEnabled &&
            batchDetails?.rejections &&
            batchDetails?.status === 'rejected' && (
              <div className="flex w-max items-center rounded-2xl px-4 py-2 text-sm">
                <span className="mr-1 font-bold">Declined by: </span>
                <ul>
                  {batchDetails.rejections.map((rejection: any) => (
                    <li
                      key={rejection.user_id}
                      className="flex flex-row items-center gap-1"
                    >
                      <span>
                        {rejection.user_id === userId
                          ? 'You'
                          : rejection.user_name}
                      </span>
                      <div className="group relative cursor-pointer">
                        <InformationCircleIcon className="m-auto size-4" />
                        <div className="invisible absolute -left-2 bottom-8 min-w-max rounded-2xl bg-content-strong px-2 py-1.5 text-white group-hover:visible">
                          <FormattedDateTime value={rejection.created_at} />
                          <svg
                            viewBox="0 0 100 100"
                            className="invisible absolute size-4 fill-content-strong group-hover:visible"
                          >
                            <polygon points="0,0 100,0 50,100" />
                          </svg>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          {(batchDetails?.status === 'waiting_review' ||
            batchDetails?.status === 'completed') &&
            isApprovalEnabled && (
              <ApprovalStatus
                batchDetails={batchDetails}
                userId={userId}
                requiredNumberOfApprovals={requiredNumberOfApprovals}
              />
            )}
        </div>
      </div>
      {canApprove && (
        <div className="flex content-between gap-4">
          <SecondaryButton
            onClick={toggleDeclineDialog}
            htmlType="submit"
            data-testid="batch-payout-decline-batch"
            danger
            className="w-32"
          >
            Decline
          </SecondaryButton>
          <SecondaryButton
            onClick={() => handleApprove()}
            htmlType="submit"
            data-testid="batch-payout-approve-batch"
            className="w-32"
          >
            Approve
          </SecondaryButton>
        </div>
      )}
    </div>
  );
};
